import {
  isMorteCoverageId,
  usesBenefitSelection,
  usesBenefitSelectionWithPercent,
  type CapitalGlobalCoverageCatalogItem,
} from '@/features/quotes/schemas/capital-global/coverages-catalog';
import { getMaxAllowedPercent } from '@/features/quotes/schemas/capital-global/coverage-limits.util';
import { formatCurrencyInputValue } from '@/shared/utils/currencyFormatter';
import { sanitizePercentDraft } from '@/shared/utils/format-percent.util';
import { Field, Switch, Text } from '@noorden-insurtech/alba-design-system';
import { useRef } from 'react';
import {
  getAppShellMain,
  restoreAppShellMainScrollTop,
} from '@/shared/utils/app-shell-main-scroll.util';
import { CoverageBenefitSelect } from './coverage-benefit-select.component';

/** Figma Step 3 (`6054:58063`): right strip = switch `w-[139px]` + `gap-[24px]` + two equal `flex-1` fields. */
const ROW_GAP = 'gap-6';

const HEADER_ROW = `flex flex-row items-center ${ROW_GAP} px-4`;

const DATA_ROW = `flex flex-col gap-2 px-4 py-3`;

const DATA_ROW_FIELDS = `flex flex-row items-center ${ROW_GAP}`;

function readSwitchChecked(details: unknown): boolean | undefined {
  if (typeof details === 'boolean') return details;
  if (
    details &&
    typeof details === 'object' &&
    'checked' in details &&
    typeof (details as { checked: unknown }).checked === 'boolean'
  ) {
    return (details as { checked: boolean }).checked;
  }
  return undefined;
}

export type CoverageCatalog = CapitalGlobalCoverageCatalogItem;

export type CoverageState = {
  enabled: boolean;
  maxIndividualCapital: string;
  percent: string;
  /**
   * Código do benefício escolhido no seletor da linha (coberturas com
   * `benefitOptions`). Vai no attach como `benefits: [...]` da cobertura.
   */
  benefitCode?: string;
};

export type CoverageFieldErrors = Record<
  string,
  {
    maxIndividualCapital?: string;
    percent?: string;
    /**
     * Erro da SELEÇÃO da cobertura, não de um campo de valor: relação /
     * exclusividade do produto, base proporcional não contratada e validação de
     * attach do InsureMO (ex.: grupos de cobertura incompatíveis).
     */
    selection?: string;
  }
>;

/* eslint-disable no-unused-vars -- callback parameter names document arity for callers */
type Props = {
  /** Título da seção (categoria oficial do catálogo). Default: "Coberturas". */
  title?: string;
  catalog: CoverageCatalog[];
  states: Record<string, CoverageState>;
  errors?: CoverageFieldErrors;
  onToggle: (id: string, enabled: boolean) => void;
  onIndividualCapitalChange: (id: string, value: string) => void;
  /** Escolha do benefício nas coberturas com `benefitOptions` (seletor). */
  onBenefitChange: (id: string, benefitCode: string) => void;
  onPercentChange: (id: string, value: string) => void;
  onPercentBlur: (id: string) => void;
  disabled?: boolean;
  morteId?: string;
};
/* eslint-enable no-unused-vars */

/**
 * Capital Global coverages table — Switch Card aligned with Figma Step 3 (`6054:58063`).
 * Required coverage keeps switch locked on; "(Automático)" in title is informational.
 */
export function CoveragesTable({
  title = 'Coberturas',
  catalog,
  states,
  errors,
  onToggle,
  onIndividualCapitalChange,
  onBenefitChange,
  onPercentChange,
  onPercentBlur,
  disabled,
  morteId,
}: Props) {
  const mainScrollBeforeToggleRef = useRef(0);

  return (
    <div className='flex flex-col gap-4'>
      <div className={HEADER_ROW}>
        <div className='min-w-0 flex-1'>
          <Text className='text-lg font-bold text-alba-black-500 text-left'>
            {title}
          </Text>
        </div>
        <div className={`flex min-w-0 flex-1 flex-row items-center ${ROW_GAP}`}>
          <span aria-hidden className='inline-block w-[139px] shrink-0' />
          <div className='flex min-w-0 flex-1 justify-center'>
            <Text className='text-sm font-bold text-alba-black-500 text-center'>
              Capital Máximo Individual
            </Text>
          </div>
          <div className='flex min-w-0 flex-1 justify-center'>
            <Text className='text-sm font-bold text-alba-black-500 text-center'>
              %
            </Text>
          </div>
        </div>
      </div>

      <div className='flex flex-col divide-y divide-alba-gray-300 rounded-xl border border-alba-gray-300 bg-white'>
        {catalog.map(coverageItem => {
          const state = states[coverageItem.id];
          if (!state) return null;
          const rowErrors = errors?.[coverageItem.id];
          const selectionError = rowErrors?.selection?.trim();
          const hasSelectionError = Boolean(selectionError);
          // Formato PME (CoverageRowShell): um único texto vermelho inline por
          // linha, com o erro de seleção (relação/exclusão) na frente por ser
          // o mais acionável.
          const rowError =
            selectionError ||
            rowErrors?.maxIndividualCapital ||
            rowErrors?.percent;
          // Cobertura contratada pela escolha do benefício (ex.: Assistência
          // Funeral Individual R$ 5/7/10 mil): seletor no lugar do input
          // livre, com o % travado porque é derivado da opção. Nas diárias
          // (`BENEFIT_SELECTION_WITH_SI`) o % fica EDITÁVEL: é o input do
          // valor da diária (% do capital individual, limitado ao teto).
          const usesBenefitSelect = usesBenefitSelection(coverageItem);
          const percentIsInput =
            !usesBenefitSelect || usesBenefitSelectionWithPercent(coverageItem);
          const isMorte = isMorteCoverageId(coverageItem.id, morteId);
          const rowLocked = disabled || isMorte;
          const switchLocked = rowLocked || coverageItem.required;
          const capitalInvalid = Boolean(
            rowErrors?.maxIndividualCapital || hasSelectionError
          );
          const percentInvalid = Boolean(
            rowErrors?.percent || hasSelectionError
          );
          return (
            <div
              key={coverageItem.id}
              className={DATA_ROW}
              onPointerDownCapture={() => {
                const m = getAppShellMain();
                mainScrollBeforeToggleRef.current = m?.scrollTop ?? 0;
              }}
            >
              <div className={DATA_ROW_FIELDS}>
                <div className='flex min-w-0 flex-1 flex-col gap-1'>
                  <div className='flex flex-wrap items-center gap-2'>
                    <Text className='text-sm font-bold text-alba-black-500'>
                      {coverageItem.title}
                    </Text>
                    {coverageItem.required && (
                      <div className='flex flex-row items-center justify-center gap-2 rounded-xl bg-[rgba(44,40,100,0.2)] px-2 py-px'>
                        <Text className='text-xs font-bold text-alba-blue-500'>
                          Básica
                        </Text>
                      </div>
                    )}
                  </div>
                  {coverageItem.description && (
                    <Text className='text-xs text-alba-gray-600'>
                      {coverageItem.description}
                    </Text>
                  )}
                </div>

                <div
                  className={`flex min-w-0 flex-1 flex-row items-center ${ROW_GAP}`}
                >
                  <div className='flex w-[139px] shrink-0 items-center justify-end'>
                    <Switch.Root
                      checked={state.enabled}
                      onCheckedChange={details => {
                        if (switchLocked) return;
                        const checked = readSwitchChecked(details);
                        if (typeof checked !== 'boolean') return;
                        const y = mainScrollBeforeToggleRef.current;
                        onToggle(coverageItem.id, checked);
                        restoreAppShellMainScrollTop(y);
                      }}
                      disabled={switchLocked}
                    >
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label className='sr-only'>
                        {coverageItem.title}
                      </Switch.Label>
                      <Switch.HiddenInput />
                    </Switch.Root>
                  </div>

                  <div className='flex min-w-0 flex-1 justify-end'>
                    {usesBenefitSelect ? (
                      <CoverageBenefitSelect
                        options={coverageItem.benefitOptions ?? []}
                        selectedCode={state.benefitCode ?? ''}
                        onSelectBenefit={benefitCode =>
                          onBenefitChange(coverageItem.id, benefitCode)
                        }
                        disabled={rowLocked || !state.enabled}
                        invalid={capitalInvalid}
                      />
                    ) : (
                      <Field.Root
                        className='min-w-0 w-full'
                        invalid={capitalInvalid}
                      >
                        <Field.Input
                          type='text'
                          inputMode='decimal'
                          value={state.maxIndividualCapital}
                          onChange={e =>
                            onIndividualCapitalChange(
                              coverageItem.id,
                              formatCurrencyInputValue(e.target.value)
                            )
                          }
                          placeholder='R$ 0,00'
                          disabled={rowLocked || !state.enabled}
                          className='rounded-xl border border-alba-gray-400 px-5 py-3 text-xs text-alba-gray-600'
                        />
                      </Field.Root>
                    )}
                  </div>

                  <div className='flex min-w-0 flex-1 justify-end'>
                    <Field.Root
                      className='min-w-0 w-full'
                      invalid={percentInvalid}
                    >
                      <Field.Input
                        type='text'
                        inputMode='decimal'
                        value={state.percent ? `${state.percent}%` : ''}
                        onChange={e =>
                          onPercentChange(
                            coverageItem.id,
                            sanitizePercentDraft(
                              e.target.value,
                              getMaxAllowedPercent(coverageItem, morteId)
                            )
                          )
                        }
                        onBlur={() => onPercentBlur(coverageItem.id)}
                        placeholder='0%'
                        disabled={
                          rowLocked || !state.enabled || !percentIsInput
                        }
                        className='rounded-xl border border-alba-gray-400 px-5 py-3 text-xs text-alba-gray-600'
                      />
                    </Field.Root>
                  </div>
                </div>
              </div>

              {rowError && (
                <div className='mt-1 text-right text-[12px] text-alba-error-red-500'>
                  {rowError}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
